import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Download, Copy, QrCode, Printer, Store, LayoutGrid, Loader2 } from "lucide-react";

const GOLD = "#C9A24B";
const INK = "#1A1A1A";
const CREAM = "#FBF7EF";
const QR_RES = 900; // internal canvas resolution for crisp downloads

const origin = () => window.location.origin;
const menuUrl = (token) => (token ? `${origin()}/menu?t=${token}` : `${origin()}/menu`);

// Load an image once (logo), resolve null if it fails.
function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Compose a premium printable poster PNG around the QR canvas.
async function buildPoster(qrCanvas, title, subtitle, logo) {
  const W = 1080;
  const H = 1500;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  // background
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);

  // outer gold frame
  ctx.lineWidth = 10;
  ctx.strokeStyle = GOLD;
  roundRect(ctx, 50, 50, W - 100, H - 100, 48);
  ctx.stroke();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(201,162,75,0.5)";
  roundRect(ctx, 74, 74, W - 148, H - 148, 36);
  ctx.stroke();

  // logo
  if (logo) {
    const lw = 420;
    const lh = (logo.height / logo.width) * lw;
    ctx.drawImage(logo, (W - lw) / 2, 150, lw, lh);
  } else {
    ctx.fillStyle = INK;
    ctx.font = "700 92px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("DOGANA", W / 2, 260);
  }

  // instruction heading
  ctx.fillStyle = INK;
  ctx.textAlign = "center";
  ctx.font = "700 64px Georgia, serif";
  ctx.fillText("Scansiona & Ordina", W / 2, 470);

  ctx.fillStyle = "#6b6257";
  ctx.font = "400 34px Helvetica, Arial, sans-serif";
  ctx.fillText(subtitle, W / 2, 530);

  // QR white plaque
  const qrSize = 620;
  const plaquePad = 40;
  const plaqueX = (W - qrSize - plaquePad * 2) / 2;
  const plaqueY = 600;
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0,0,0,0.12)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 18;
  roundRect(ctx, plaqueX, plaqueY, qrSize + plaquePad * 2, qrSize + plaquePad * 2, 36);
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.drawImage(qrCanvas, plaqueX + plaquePad, plaqueY + plaquePad, qrSize, qrSize);

  // title (table name / menu)
  ctx.fillStyle = INK;
  ctx.font = "700 58px Georgia, serif";
  ctx.fillText(title, W / 2, plaqueY + qrSize + plaquePad * 2 + 110);

  // footer line
  ctx.fillStyle = GOLD;
  ctx.font = "600 30px Helvetica, Arial, sans-serif";
  ctx.fillText("Inquadra il codice con la fotocamera del telefono", W / 2, H - 120);

  return c;
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

function slug(s) {
  return String(s || "qr").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function QrItem({ title, subtitle, token, logo }) {
  const ref = useRef(null);
  const url = menuUrl(token);

  const getCanvas = () => ref.current?.querySelector("canvas");

  const downloadQr = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    downloadDataUrl(canvas.toDataURL("image/png"), `qr-${slug(title)}.png`);
    toast.success("QR scaricato in alta risoluzione");
  };

  const downloadPoster = async () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const poster = await buildPoster(canvas, title, subtitle, logo);
    downloadDataUrl(poster.toDataURL("image/png"), `locandina-${slug(title)}.png`);
    toast.success("Locandina scaricata");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); }
    catch { toast.error("Copia non riuscita"); }
  };

  return (
    <Card className="p-4 rounded-2xl flex flex-col items-center text-center border-border" data-testid={`qr-item-${slug(title)}`}>
      <div ref={ref} className="bg-white p-2 rounded-xl border border-border shadow-sm">
        <QRCodeCanvas
          value={url}
          size={QR_RES}
          level="H"
          fgColor={INK}
          bgColor="#ffffff"
          includeMargin
          style={{ width: 150, height: 150 }}
          data-testid={`qr-canvas-${slug(title)}`}
        />
      </div>
      <h3 className="font-dash font-semibold text-base mt-3 text-foreground">{title}</h3>
      <p className="text-[11px] text-muted-foreground break-all mt-1 leading-tight px-1">{url}</p>
      <div className="flex flex-wrap gap-2 justify-center mt-3 w-full">
        <Button onClick={downloadPoster} size="sm" className="rounded-full flex-1 min-w-[130px]" data-testid={`download-poster-${slug(title)}`}>
          <Printer className="w-4 h-4 mr-1" /> Locandina
        </Button>
        <Button onClick={downloadQr} size="sm" variant="outline" className="rounded-full flex-1 min-w-[110px]" data-testid={`download-qr-${slug(title)}`}>
          <Download className="w-4 h-4 mr-1" /> QR PNG
        </Button>
        <Button onClick={copyLink} size="sm" variant="ghost" className="rounded-full" data-testid={`copy-link-${slug(title)}`}>
          <Copy className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}

export default function QRCodes() {
  const [tables, setTables] = useState([]);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadImage("/logo-dogana.png").then(setLogo);
    api.get("/tables")
      .then(({ data }) => setTables(data || []))
      .catch(() => setTables([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8 pb-10" data-testid="qr-codes-page">
      <div>
        <div className="flex items-center gap-2 text-primary">
          <QrCode className="w-6 h-6" />
          <h1 className="font-dash text-2xl font-bold text-foreground">QR Codes</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Scarica i QR in alta risoluzione o la locandina brandizzata pronta da stampare. Il menù generale è la fonte: i tavoli mostrano gli stessi piatti.
        </p>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Store className="w-4 h-4 text-primary" />
          <h2 className="font-dash font-semibold text-lg">Menù Generale</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <QrItem title="Menù Dogana" subtitle="Il nostro menù digitale" token={null} logo={logo} />
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <LayoutGrid className="w-4 h-4 text-primary" />
          <h2 className="font-dash font-semibold text-lg">Tavoli ({tables.length})</h2>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento tavoli…</div>
        ) : tables.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground rounded-2xl">
            Nessun tavolo configurato. Creali in <span className="font-medium text-foreground">Tavoli & Ordini → Gestione tavoli</span>.
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {tables.filter((t) => t.public_token).map((t) => (
              <QrItem
                key={t.id}
                title={t.name || `Tavolo ${t.number}`}
                subtitle={t.room ? `${t.room} · Ordina dal tavolo` : "Ordina dal tavolo"}
                token={t.public_token}
                logo={logo}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
